import { Popover, Transition } from '@headlessui/react';
import { TrashIcon } from '@heroicons/react/solid';
import { prebuiltSegments } from 'components/segments/prebuiltSegments';
import { ButtonCTASmall, LoadingSpinner, Tooltip } from 'components/ui';
import { Fragment } from 'react';
import { useAppDispatch, useAppSelector } from 'store';
import {
  clearApplied,
  composableFiltersState,
} from 'store/composableFiltersSlice';
import { useDeleteSegmentMutation } from 'store/services/segmentsApi';
import { useParseURLQueryParams } from 'utils';

export const RemoveSegment = () => {
  const { serializedStoreName } = useParseURLQueryParams();

  const { selected } = useAppSelector(composableFiltersState);

  const currentSegment = selected.segment;

  const isSegmentPrebuilt = prebuiltSegments.includes(
    String(currentSegment?.segment_name)
  );

  const isManagedSegment = Boolean(
    currentSegment?.json_filter?.filter?.filters?.find((filter) =>
      filter.name?.startsWith('managed_segment')
    )
  );

  const dispatch = useAppDispatch();

  const [deleteSegment, { isLoading }] = useDeleteSegmentMutation();

  const handleDelete = (close: Function) => {
    if (!!!currentSegment) return;

    deleteSegment({
      segmentId: Number(currentSegment?.id),
      serializedStoreName: String(serializedStoreName),
    })
      .unwrap()
      .then(() => {
        dispatch(clearApplied());
        close();
      });
  };

  return (
    <Popover className="relative">
      {({ open, close }) => (
        <>
          <Popover.Button
            disabled={isSegmentPrebuilt || isManagedSegment}
            className={`
                ${open ? '' : 'text-opacity-90'}
                text-segments-link group px-3 py-2 text-base font-medium hover:text-opacity-100 disabled:opacity-50 disabled:cursor-not-allowed `}
          >
            <Tooltip
              text={
                isSegmentPrebuilt || isManagedSegment
                  ? 'Pre-built / premium segments can not be deleted'
                  : 'Delete segment'
              }
            >
              <TrashIcon className="w-5 h-5 fill-current text-segments-at-risk" />
            </Tooltip>
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel className="absolute z-10 w-screen max-w-sm mt-1 transform -translate-x-1/2 left-1/2 lg:max-w-sm ">
              <div className="bg-white shadow-2xl rounded-3xl ring-1 ring-black ring-opacity-5">
                <div className="pt-6 px-7">
                  <p className="font-bold">Remove segment</p>
                </div>
                <form
                  className="py-2 text-gray-700 px-7"
                  onSubmit={(e) => {
                    e.preventDefault();
                    handleDelete(close);
                  }}
                >
                  <div className="mt-2">
                    <p>
                      Are you sure you want to delete the{' '}
                      <span className="font-bold">
                        {currentSegment?.segment_name}
                      </span>{' '}
                      segment?
                    </p>
                    {currentSegment?.auto_synced && (
                      <p className="mt-2 text-segments-danger text-segments-13px">
                        This segment has auto-sync on. Deleting a synced segment
                        will break the live-sync between Segments and your
                        marketing platforms.
                      </p>
                    )}
                  </div>

                  <div className="flex justify-end mt-2 mb-2 space-x-4">
                    <div>
                      <ButtonCTASmall
                        variant="secondary"
                        type="button"
                        onClick={() => {
                          close();
                        }}
                      >
                        <p className="px-4 font-normal text-segments-13px">
                          Cancel
                        </p>
                      </ButtonCTASmall>
                    </div>
                    <div>
                      <ButtonCTASmall variant="danger" type="submit">
                        <div className="w-20 font-bold text-segments-13px">
                          {isLoading ? (
                            <LoadingSpinner className="w-5 h-5 mx-auto text-white" />
                          ) : (
                            'Delete'
                          )}
                        </div>
                      </ButtonCTASmall>
                    </div>
                  </div>
                </form>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};
